import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import DashboardLayout from '@/components/DashboardLayout';
import MessageSoundProvider from '@/components/MessageSoundProvider';
import WorkstationPulsePage from '@/pages/WorkstationPulsePage';
import AgentDownloadsPage from '@/pages/AgentDownloadsPage';
import AgentReleasesAdminPage from '@/pages/AgentReleasesAdminPage';
import KpiCockpitPage from '@/pages/KpiCockpitPage';
import MyKpisPage from '@/pages/MyKpisPage';
import HrPulsePage from '@/pages/HrPulsePage';
import NomenclatureEnginePage from '@/pages/NomenclatureEnginePage';
import RevisionsInboxPage from '@/pages/RevisionsInboxPage';
import LoginPage from '@/pages/LoginPage';
import AdminDashboard from '@/pages/AdminDashboard';
import ProductionDashboard from '@/pages/ProductionDashboard';
import ProjectsPage from '@/pages/ProjectsPage';
import TasksPage from '@/pages/TasksPage';
import FreelancerDashboard from '@/pages/FreelancerDashboard';
import ClientDashboard from '@/pages/ClientDashboard';
import AccountantDashboard from '@/pages/AccountantDashboard';
import AIAgentChat from '@/pages/AIAgentChat';
import UsersPage from '@/pages/UsersPage';
import AuditPage from '@/pages/AuditPage';
import ResourcesPage from '@/pages/ResourcesPage';
import ContractsPage from '@/pages/ContractsPage';
import BidEstimationPage from '@/pages/BidEstimationPage';
import TemplatesPage from '@/pages/TemplatesPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import DisputesPage from '@/pages/DisputesPage';
import RegisterPage from '@/pages/RegisterPage';
import ProfilePage from '@/pages/ProfilePage';
import VendorRegisterPage from '@/pages/VendorRegisterPage';
import VendorDashboard from '@/pages/VendorDashboard';
import VendorCommandCenter from '@/pages/VendorCommandCenter';
import VendorTasksPage from '@/pages/VendorTasksPage';
import VendorTeamPage from '@/pages/VendorTeamPage';
import VendorTrackerPage from '@/pages/VendorTrackerPage';
import VendorFinancePage from '@/pages/VendorFinancePage';
import VendorWorkOrdersPage from '@/pages/VendorWorkOrdersPage';
import VendorTicketPage from '@/pages/VendorTicketPage';
import AllotmentPage from '@/pages/AllotmentPage';
import FreelancerRegisterPage from '@/pages/FreelancerRegisterPage';
import FreelancerDashboardNew from '@/pages/FreelancerDashboardNew';
import EmployeeDashboard from '@/pages/EmployeeDashboard';
import BotsPage from '@/pages/BotsPage';
// NamingRulesPage deprecated — replaced by NomenclatureEnginePage (May 18 2026)
import AdminLiveView from '@/pages/AdminLiveView';
import ClientReviewPage from '@/pages/ClientReviewPage';
import SubmitForApprovalPage from '@/pages/SubmitForApprovalPage';
import SubmitForApprovalTreePage from '@/pages/SubmitForApprovalTreePage';
import ArchitectureMap from '@/pages/ArchitectureMap';
import ClientCreatePage from '@/pages/ClientCreatePage';
import ClientPortfolioPage from '@/pages/ClientPortfolioPage';
import EmailCampaignsPage from '@/pages/EmailCampaignsPage';
import AdminToolsPage from '@/pages/AdminToolsPage';
import EmergencyAdminPage from '@/pages/EmergencyAdminPage';
import ProjectGanttPage from '@/pages/ProjectGanttPage';
import NotificationsPage from '@/pages/NotificationsPage';
import ProjectMembersPage from '@/pages/ProjectMembersPage';
import HRCommandCenter from '@/pages/HRCommandCenter';
import PersonProfilePage from '@/pages/PersonProfilePage';
import HRVocabularies from '@/pages/HRVocabularies';
import ClientProfilePage from '@/pages/ClientProfilePage';
import ClientMessageCenter from '@/pages/ClientMessageCenter';
import MessageCenterV3 from '@/pages/MessageCenterV3';
import MessagesInbox from '@/pages/MessagesInbox';
import AnimatorAllotmentPage from '@/pages/AnimatorAllotmentPage';
import AdminAllotmentPage from '@/pages/AdminAllotmentPage';
import MaintenanceTreePage from '@/pages/MaintenanceTreePage';
import ProjectRateCardPage from '@/pages/ProjectRateCardPage';
import MyApprovalsPage from '@/pages/MyApprovalsPage';
import SupervisorFeedPage from '@/pages/SupervisorFeedPage';

/**
 * LoginOrRedirect — if already authed, send the user back to the
 * ?next= page they came from (set by ProtectedRoute / AuthContext
 * handleAuthFail). Falls back to /dashboard. Fixes the testing-agent
 * regression where ?next= was being silently dropped after re-login.
 */
function LoginOrRedirect() {
  const { user } = useAuth();
  const location = useLocation();
  if (user) {
    const params = new URLSearchParams(location.search);
    const next = params.get('next');
    // Only allow same-origin relative paths to prevent open-redirect.
    const safeNext = next && next.startsWith('/') && !next.startsWith('//')
      ? next
      : '/dashboard';
    return <Navigate to={safeNext} replace />;
  }
  return <LoginPage />;
}


function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-[#09090b] flex items-center justify-center"><div className="animate-pulse-slow text-zinc-600 text-sm font-mono">VawworldOS Loading...</div></div>;
  if (!user) {
    // Preserve the original location so AuthContext.handleAuthFail's
    // ?next= (and the LoginPage's redirect-after-login) both land the
    // user back where they were. Testing-agent caught (Jun 13 2026)
    // that without this, the Navigate stripped the ?next= every time.
    const next = encodeURIComponent(location.pathname + (location.search || ''));
    const target = location.pathname.startsWith('/login')
      ? '/login'
      : `/login?next=${next}`;
    return <Navigate to={target} replace />;
  }
  return <DashboardLayout>{children}</DashboardLayout>;
}

// Submit-for-Approval router: PM/Admin get the 2-pane tree; everyone else (artists/freelancers) keeps the legacy single form.
function SubmitRouter() {
  const { user } = useAuth();
  if (['master_admin', 'production_manager', 'supervisor', 'creative_director'].includes(user?.role)) {
    return <SubmitForApprovalTreePage />;
  }
  return <SubmitForApprovalPage />;
}

// Vawworld Agent — production-side management tool only. External vendor
// producers / vendor artists should not see internal AI tools. Founder
// ask Feb 14, 2026.
function AIAgentGuard({ children }) {
  const { user } = useAuth();
  const BLOCKED = new Set(['vendor_producer', 'vendor_artist',
                            'client_producer', 'client_director']);
  if (BLOCKED.has(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Review-tool gate (founder ask Feb 2026): only Production / Reviewing
// roles + Clients reach /reviews. Artists (employees, freelancers,
// vendor L1/L2/L3) read producer feedback ONLY via the "Producer
// Notes" chip on their MY TASKS row — they cannot enter the review UI
// directly. Direct URL hits return a friendly Forbidden screen with
// a CTA back to My Tasks.
const REVIEW_ALLOWED_ROLES = new Set([
  'master_admin', 'production_manager', 'supervisor',
  'cg_supervisor', 'lighting_supervisor', 'vfx_supervisor',
  'creative_director', 'studio_director', 'director',
  'art_director', 'animation_director', 'technical_director',
  'executive_producer', 'line_producer',
  'client_producer', 'client_director',
]);
function ReviewRoute({ children }) {
  const { user } = useAuth();
  if (!REVIEW_ALLOWED_ROLES.has(user?.role)) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <p className="text-[10px] uppercase tracking-widest text-rose-400 font-bold mb-2">Access restricted</p>
          <h1 className="text-xl text-white font-medium mb-2">The Review tool is for Production roles only</h1>
          <p className="text-[12px] text-zinc-400 leading-relaxed mb-6">
            Artists, freelancers and vendor teams don't enter the Approvals UI. Your producer relays client feedback to you.
            You'll find every producer note + revision request as a chip on the matching task row on <strong>My Tasks</strong>.
          </p>
          <a href="/dashboard"
            data-testid="review-forbidden-back"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-[11px] font-medium bg-violet-500/20 hover:bg-violet-500/30 text-violet-200 border border-violet-500/40">
            Go to My Tasks
          </a>
        </div>
      </div>
    );
  }
  return children;
}

// Profile router: clients get a clean account page (no employee Documents/
// Leave/Performance leak); everyone else keeps the existing employee profile.
function ProfileRouter() {
  const { user } = useAuth();
  if (user?.role === 'client_producer' || user?.role === 'client_director') {
    return <ClientProfilePage />;
  }
  return <ProfilePage />;
}

function DashboardRouter() {
  const { user } = useAuth();
  if (!user) return null;
  // Internal employees (artist-level workforce_type=employee) → EmployeeDashboard
  const SPECIAL_ROLES = new Set([
    'master_admin', 'production_manager', 'accountant', 'supervisor', 'hr_manager',
    'creative_director', 'vendor_producer', 'vendor_artist',
    'client_producer', 'client_director', 'freelancer',
  ]);
  if (user.workforce_type === 'employee' && !SPECIAL_ROLES.has(user.role)) {
    return <EmployeeDashboard />;
  }
  switch (user.role) {
    case 'master_admin': return <AdminDashboard />;
    case 'production_manager': return <AdminDashboard />;
    case 'accountant': return <AccountantDashboard />;
    case 'supervisor': return <ClientReviewPage />;
    case 'hr_manager': return <UsersPage />;
    case 'vendor_producer': return <VendorDashboard />;
    case 'vendor_artist': return <FreelancerDashboardNew />;
    case 'client_producer': return <ClientDashboard />;
    case 'client_director': return <ClientDashboard />;
    case 'freelancer': return <FreelancerDashboardNew />;
    default: return <AdminDashboard />;
  }
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-[#09090b] flex items-center justify-center"><div className="animate-pulse-slow text-zinc-600 text-sm font-mono">VawworldOS Loading...</div></div>;

  return (
    <ErrorBoundary key={location.pathname} pageName={location.pathname}>
    <Routes>
      <Route path="/login" element={<LoginOrRedirect />} />
      {/* Public: artists hit vawworld.in/agent-downloads from any PC to grab the installer */}
      <Route path="/agent-downloads" element={<AgentDownloadsPage />} />
      <Route path="/register/:code" element={<RegisterPage />} />
      <Route path="/vendor/register" element={<VendorRegisterPage />} />
      <Route path="/vendor/register/:code" element={<VendorRegisterPage />} />
      <Route path="/freelancer/register" element={<FreelancerRegisterPage />} />
      {/* Freelancer sub-pages (all render the same component with different default tab) */}
      <Route path="/fl-tasks" element={<ProtectedRoute><FreelancerDashboardNew /></ProtectedRoute>} />
      <Route path="/my-approvals" element={<ProtectedRoute><MyApprovalsPage /></ProtectedRoute>} />
      <Route path="/fl-tracker" element={<ProtectedRoute><FreelancerDashboardNew /></ProtectedRoute>} />
      <Route path="/fl-earnings" element={<ProtectedRoute><FreelancerDashboardNew /></ProtectedRoute>} />
      <Route path="/fl-communicate" element={<ProtectedRoute><FreelancerDashboardNew /></ProtectedRoute>} />
      {/* Employee sub-pages */}
      <Route path="/em-tasks" element={<ProtectedRoute><EmployeeDashboard /></ProtectedRoute>} />
      <Route path="/em-references" element={<ProtectedRoute><EmployeeDashboard /></ProtectedRoute>} />
      <Route path="/em-leaves" element={<ProtectedRoute><EmployeeDashboard /></ProtectedRoute>} />
      <Route path="/em-salary" element={<ProtectedRoute><EmployeeDashboard /></ProtectedRoute>} />
      <Route path="/em-communicate" element={<ProtectedRoute><EmployeeDashboard /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />
      {/* Production */}
      <Route path="/production" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
      <Route path="/pipeline" element={<ProtectedRoute><ProductionDashboard /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/submit" element={<ProtectedRoute><SubmitRouter /></ProtectedRoute>} />
      <Route path="/architecture" element={<ProtectedRoute><ArchitectureMap /></ProtectedRoute>} />
      <Route path="/maintenance-tree" element={<ProtectedRoute><MaintenanceTreePage /></ProtectedRoute>} />
      <Route path="/clients/new" element={<ProtectedRoute><ClientCreatePage /></ProtectedRoute>} />
      <Route path="/clients/portfolio" element={<ProtectedRoute><ClientPortfolioPage /></ProtectedRoute>} />
      <Route path="/admin/email" element={<ProtectedRoute><EmailCampaignsPage /></ProtectedRoute>} />
      <Route path="/admin/tools" element={<ProtectedRoute><AdminToolsPage /></ProtectedRoute>} />
      <Route path="/admin/emergency" element={<ProtectedRoute><EmergencyAdminPage /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
      {/* People */}
      <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
      <Route path="/hr" element={<ProtectedRoute><HRCommandCenter /></ProtectedRoute>} />
      <Route path="/hr/people/:id" element={<ProtectedRoute><PersonProfilePage /></ProtectedRoute>} />
      <Route path="/hr/vocabularies" element={<ProtectedRoute><HRVocabularies /></ProtectedRoute>} />
      <Route path="/contracts" element={<ProtectedRoute><ContractsPage /></ProtectedRoute>} />
      <Route path="/resources" element={<ProtectedRoute><ResourcesPage /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
      {/* Finance */}
      <Route path="/accounting" element={<ProtectedRoute><AccountantDashboard /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><AccountantDashboard /></ProtectedRoute>} />
      <Route path="/payouts" element={<ProtectedRoute><AccountantDashboard /></ProtectedRoute>} />
      <Route path="/bid-estimation" element={<ProtectedRoute><BidEstimationPage /></ProtectedRoute>} />
      {/* AI */}
      <Route path="/ai-agent" element={<ProtectedRoute><AIAgentGuard><AIAgentChat /></AIAgentGuard></ProtectedRoute>} />
      {/* Analytics */}
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      {/* Disputes */}
      <Route path="/disputes" element={<ProtectedRoute><DisputesPage /></ProtectedRoute>} />
      <Route path="/vendors" element={<ProtectedRoute><VendorCommandCenter /></ProtectedRoute>} />
      <Route path="/admin/live-view" element={<ProtectedRoute><AdminLiveView /></ProtectedRoute>} />
      <Route path="/allotment" element={<ProtectedRoute><AllotmentPage /></ProtectedRoute>} />
      <Route path="/cba-allotment" element={<ProtectedRoute><AdminAllotmentPage /></ProtectedRoute>} />
      <Route path="/projects/:projectId/rate-card" element={<ProtectedRoute><ProjectRateCardPage /></ProtectedRoute>} />
      <Route path="/rate-card" element={<ProtectedRoute><ProjectRateCardPage /></ProtectedRoute>} />
      <Route path="/my-allotment" element={<ProtectedRoute><AnimatorAllotmentPage /></ProtectedRoute>} />
      <Route path="/vendor-tasks" element={<ProtectedRoute><VendorTasksPage /></ProtectedRoute>} />
      <Route path="/vendor-tracker" element={<ProtectedRoute><VendorTrackerPage /></ProtectedRoute>} />
      <Route path="/vendor-team" element={<ProtectedRoute><VendorTeamPage /></ProtectedRoute>} />
      <Route path="/vendor-finance" element={<ProtectedRoute><VendorFinancePage /></ProtectedRoute>} />
      <Route path="/vendor-work-orders" element={<ProtectedRoute><VendorWorkOrdersPage /></ProtectedRoute>} />
      <Route path="/vendor-ticket" element={<ProtectedRoute><VendorTicketPage /></ProtectedRoute>} />
      {/* Client */}
      <Route path="/milestones" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessageCenterV3 /></ProtectedRoute>} />
      <Route path="/messages-legacy" element={<ProtectedRoute><ClientMessageCenter /></ProtectedRoute>} />
      <Route path="/mentions" element={<ProtectedRoute><MessagesInbox /></ProtectedRoute>} />
      {/* Payments & Change Orders removed from client portal per user request (Apr 2026) */}      {/* Freelancer */}
      <Route path="/earnings" element={<ProtectedRoute><FreelancerDashboard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfileRouter /></ProtectedRoute>} />
      <Route path="/profile/:userId" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      {/* Security */}
      <Route path="/audit" element={<ProtectedRoute><AuditPage /></ProtectedRoute>} />
      {/* VOS Studio Agent — Workstation Pulse (Phase 1 Feb 16 2026) */}
      <Route path="/admin/workstation-pulse" element={<ProtectedRoute><WorkstationPulsePage /></ProtectedRoute>} />
      {/* VOS Studio Agent — Drag-drop installer uploads (vibe-coder mode May 17 2026) */}
      <Route path="/admin/agent-releases" element={<ProtectedRoute><AgentReleasesAdminPage /></ProtectedRoute>} />
      {/* VOS Studio Agent — Phase 2 KPI dashboards (May 18 2026) */}
      <Route path="/admin/kpi-cockpit" element={<ProtectedRoute><KpiCockpitPage /></ProtectedRoute>} />
      <Route path="/admin/hr-pulse" element={<ProtectedRoute><HrPulsePage /></ProtectedRoute>} />
      <Route path="/dashboard/my-kpis" element={<ProtectedRoute><MyKpisPage /></ProtectedRoute>} />
      {/* V2 Nomenclature Engine — solves cross-task pickup disasters (May 18 2026 founder ask) */}
      <Route path="/admin/nomenclature-engine" element={<ProtectedRoute><NomenclatureEnginePage /></ProtectedRoute>} />
      {/* Revisions inbox — surfaces pin notes for all working teams */}
      <Route path="/revisions-inbox" element={<ProtectedRoute><RevisionsInboxPage /></ProtectedRoute>} />
      {/* Bots */}
      <Route path="/bots" element={<ProtectedRoute><BotsPage /></ProtectedRoute>} />
      {/* Legacy naming page — redirect to the V2 Nomenclature Engine */}
      <Route path="/naming" element={<Navigate to="/admin/nomenclature-engine" replace />} />
      {/* Approvals & Gantt */}
      <Route path="/reviews" element={<ProtectedRoute><ReviewRoute><ClientReviewPage /></ReviewRoute></ProtectedRoute>} />
      <Route path="/reviews/:id" element={<ProtectedRoute><ReviewRoute><ClientReviewPage /></ReviewRoute></ProtectedRoute>} />
      <Route path="/supervisor-feed" element={<ProtectedRoute><SupervisorFeedPage /></ProtectedRoute>} />
      <Route path="/gantt" element={<ProtectedRoute><ProjectGanttPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/members" element={<ProtectedRoute><ProjectMembersPage /></ProtectedRoute>} />
      {/* Catch-all */}
      <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} replace />} />
    </Routes>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <ErrorBoundary pageName="VawworldOS">
      <BrowserRouter>
        <AuthProvider>
          <MessageSoundProvider />
          <Toaster richColors position="top-right" closeButton />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
